// Copyright (c) Bentley Systems
import {
  ActionColumn,
  Button,
  DefaultCell,
  DropdownMenu,
  IconButton,
  InformationPanel,
  InformationPanelBody,
  InformationPanelContent,
  InformationPanelHeader,
  InformationPanelWrapper,
  ProgressRadial,
  SearchBox,
  Table,
  tableFilters,
  TableFilterValue,
  TablePaginator,
  TablePaginatorRendererProps,
  Text,
  Tile,
} from "@itwin/itwinui-react";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ElistDataModel } from "./DataModels/elist-data-model";
import { ElistStructureModel } from "./DataModels/elist-structure-model";
import { ElistViewModel } from "./ViewModels/elist-view-model";
import { Column } from "./ViewModels/column";
import Action from "../Action/Action";
import { selectAccessToken } from "../app/authorization-slice";
import { CellVm } from "./ViewModels/cell-vm";
import { ElistFilter } from "./ViewModels/elist-filter";
import {
  customCellValueSort,
  FormatDate,
  getCheckBoxStyleFromString,
  getIndicesOf,
  getJsonFromParameters,
  getParametersFromUrlString,
  getQueryParam,
  getstyleFromString,
  getTotalHeightWithMargins,
  getTranslatedStringFromParameters,
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { SvgLabel, SvgMore } from "@itwin/itwinui-icons-react";
import { LegendModal } from "./Legend/LegendModal";
import * as React from "react";
import Variant from "./Variant/Variant";
import Eform from "../Eform/Eform";
import IconByName from "../Icons/IconByName";
import {
  selectHeaders,
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
  setActiveAreaId,
  setActiveNodeId,
  setHeader,
  setHeaders,
} from "../app/header-slice";
import {
  selectSortFilterState,
  setSortFilterForNode,
} from "../app/sort-filter-slice";
import { ElistOrder } from "./ViewModels/elist-order";
import { selectConfirmation } from "../app/confirmation-slice";
import { setformOpen } from "../app/form-open-slice";
import TableHeaderCellItemAction from "./TableHeaderCellItemAction";
import {
  CellRendererProps,
  Row,
} from "@itwin/itwinui-react/react-table";
import { AreaModel } from "../Areas/Models/area-model";
import { getComponentByIdentifier } from "../Helpers/component-registry";

const Elist = () => {
  const headerDivRef = useRef<HTMLDivElement>(null);
  const headerButtonsDivRef = useRef<HTMLDivElement>(null);
  const variantButtonsDivRef = useRef<HTMLDivElement>(null);
  const [headerDivHeight, setHeaderDivHeight] = useState<number>(0);
  const [headerButtonsDivHeight, setHeaderButtonsDivHeight] =
    useState<number>(0);
  const [variantButtonsDivHeight, setVariantButtonsDivHeight] =
    useState<number>(0);
  const dispatch = useAppDispatch();
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);
  const [appAreas, setAppAreas] = useState<AreaModel[] | undefined>();
  const [appHeader, setAppHeader] = useState<string | undefined>();
  const [areaId, setAreaId] = useState<number | undefined>();

  const [structureModel, setStructureModel] = useState<ElistStructureModel>();
  const [viewModel, setViewModel] = useState<ElistViewModel>();
  const [vmColumns, setVmColumns] = useState<Column[]>([]);
  const [vmValues, setVmValues] = useState<CellVm[][]>([]);

  const [activeVariantId, setActiveVariantId] = useState<number | undefined>();
  const [isLoading, setLoading] = useState<boolean>(true);

  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>();
  const [elistFilters, setElistFilters] = useState<ElistFilter[]>([]);
  const { id, params } = useParams();
  const accessToken = useAppSelector(selectAccessToken);
  const [fRollUrl, setFrollUrl] = React.useState<string>();
  const [propertyForFrollRefresh, setPropertyForFrollRefresh] =
    React.useState<boolean>(false);
  const [wasEformChangedAndRefreshed, setwasEformChangedAndRefreshed] =
    React.useState<boolean>(false);
  const [infoPanelHeader, setInfoPanelHeader] = React.useState<string>();
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const iTwinQueryParam = getQueryParam("iTwinId");
  const areas = useAppSelector(selectHeaders);
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const sortAndFilterState = useAppSelector((state) =>
    selectSortFilterState(state, id),
  );
  const confirmationState = useAppSelector(selectConfirmation);
  const navigate = useNavigate();
  const isBackToEntryScreenQueryParameter = getQueryParam("backToEntryScreen");

  useEffect(() => {
    const calculateHeights = () => {
      setTimeout(() => {
        if (headerDivRef.current) {
          const element = headerDivRef.current;
          const totalHeight = getTotalHeightWithMargins(element);
          setHeaderDivHeight(totalHeight);
        }
        if (headerButtonsDivRef.current) {
          const element = headerButtonsDivRef.current;
          const totalHeight = getTotalHeightWithMargins(element);
          setHeaderButtonsDivHeight(totalHeight);
        }
        if (variantButtonsDivRef.current) {
          const element = variantButtonsDivRef.current;
          const totalHeight = getTotalHeightWithMargins(element);
          setVariantButtonsDivHeight(totalHeight);
        }
      }, 0);
    };
    // Calculate heights initially
    calculateHeights();

    // Add resize event listener
    window.addEventListener("resize", calculateHeights);

    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener("resize", calculateHeights);
    };
  }, [viewModel?.header]);

  const searchBoxOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const searchStr = event.currentTarget.value.toLowerCase();
      setSearchString(searchStr);
    },
    [],
  );

  function navigateToInfPanelFunction(navFrolUrl: string) {
    setInfoPanelHeader(undefined);
    //to force refresh form in roll out window when url is the same
    if (fRollUrl === navFrolUrl) {
      setPropertyForFrollRefresh(!propertyForFrollRefresh);
      setwasEformChangedAndRefreshed(true);
    }
    setFrollUrl(navFrolUrl);
  }
  useEffect(() => {
    // do not fetch areas for main menu in entry screen (no main menu there)
    if (isBackToEntryScreenQueryParameter === "true") {
      return;
    }
    if (areas.length > 0) {
      setAppAreas(areas);
    } else {
      const abortController = new AbortController();
      const url = getUrlBasedOnEnv("/api/Structure/GetAppAreas");
      const getAreasRunId = crypto.randomUUID();
      fetch(url, {
        method: "GET",
        headers: {
          Authorization: accessToken,
          RunId: getAreasRunId,
        },
        signal: abortController.signal,
      })
        .then(async function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, getAreasRunId);
          } else {
            return response.json();
          }
        })
        .then(function (data) {
          if (!data) {
            return;
          }
          setAppAreas(data.areas);
          if (data.header) {
            setAppHeader(data.header);
          }
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            setErrorState(500, dispatch, getAreasRunId, true);
          }
        });

      // Cleanup function
      return () => {
        abortController.abort();
      };
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (appAreas && appHeader) {
      dispatch(setHeaders(appAreas));
      dispatch(setHeader(appHeader));
      dispatch(setActiveAreaId(areaId));
      dispatch(setActiveNodeId(Number(id)));
    }
  }, [accessToken, appAreas, dispatch, areaId, appHeader]);

  const getListData = useCallback(
    async (generateColumns: boolean = true) => {
      if (structureModel !== undefined && structureModel.nodeId !== undefined) {
        let urlData = getUrlBasedOnEnv(
          `/api/Data/GetListData/${  structureModel.nodeId}`,
        );
        const jsonBody = getJsonFromParameters(urlParameters);
        const selectedVariantId =
          activeVariantId !== undefined
            ? activeVariantId
            : structureModel.defaultVariant?.variantId;
        if (selectedVariantId !== undefined) {
          urlData += `?variantId=${  selectedVariantId}`;
        }
        const dataRunId = crypto.randomUUID();
        setLoading(true);
        await fetch(urlData, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: dataRunId,
            ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
          },
          body: jsonBody,
        })
          .then(async function (response) {
            if (!response.ok) {
              setErrorState(response.status, dispatch, dataRunId);
            } else {
              return response.json();
            }
          })
          .then(function (dataResponse) {
            if (!dataResponse) {
              return;
            }
            const newDataModel = new ElistDataModel(dataResponse);
            const newviewModel = new ElistViewModel(
              structureModel,
              newDataModel,
              urlParameters,
              selectedVariantId,
            );
            if (generateColumns) {
              setVmColumns(newviewModel.columns);
            }
            setViewModel(newviewModel);
            setVmValues(newviewModel.values);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [accessToken, structureModel, activeVariantId, dispatch],
  );

  const actionEnded = useCallback(() => {
    void getListData(false);
  }, [getListData]);

  const onFrollClose = useCallback(
    (refreshList: boolean) => {
      setFrollUrl(undefined);
      setwasEformChangedAndRefreshed(false);
      setInfoPanelHeader(undefined);
      if (refreshList) {
        actionEnded();
      }
    },
    [actionEnded],
  );

  const onSetInfoPanelHeader = useCallback((header: string) => {
    setInfoPanelHeader(header);
  }, []);

  const setFilterFromParameters = useCallback(
    (elStructureModel: ElistStructureModel) => {
      const tf: ElistFilter[] = [];
      if (
        elStructureModel.filterParameterPrefix !== undefined &&
        elStructureModel.filterParameterPrefix !== ""
      ) {
        const filterPrefix = elStructureModel.filterParameterPrefix;
        const filtersFromUrl = urlParameters.filter(
          (p) => p.name?.indexOf(filterPrefix) === 0,
        );
        filtersFromUrl.forEach((f) => {
          const filterId = (f.name as string).replace(filterPrefix, "");
          tf.push(new ElistFilter(filterId, f.value));
        });
        if (tf.length > 0) {
          setElistFilters(tf);
          const copySortFilterState = {
            sortBy: [...sortAndFilterState.sortBy],
            filters: tf.map((f) => {
              return { id: f.id, value: f.value };
            }),
          };
          dispatch(
            setSortFilterForNode({
              nodeId: id,
              sortFilterState: copySortFilterState,
            }),
          );
        }
      }
      //eg. after db call apply all filter as before from initial state
      if (
        sortAndFilterState.filters !== undefined &&
        sortAndFilterState.filters.length > 0 &&
        tf.length < sortAndFilterState.filters.length
      ) {
        for (const filter of sortAndFilterState.filters) {
          const elistFilter = tf.find((ef) => ef.id === filter.id);
          if (elistFilter === undefined) {
            tf.push(
              new ElistFilter(
                filter.id,
                filter.value,
              ),
            );
          }
        }
        setElistFilters(tf);
      }
    },
    [
      dispatch,
      id,
      sortAndFilterState.filters,
      sortAndFilterState.sortBy,
      urlParameters,
    ],
  );

  useEffect(() => {
    if (fRollUrl === undefined) {
      dispatch(setformOpen({ isOpen: false }));
    } else {
      dispatch(setformOpen({ isOpen: true }));
    }
  }, [dispatch, fRollUrl]);

  useEffect(() => {
    if (structureModel !== undefined) {
      void getListData();
    }
  }, [activeVariantId, structureModel, getListData]);

  useEffect(() => {
    if (structureModel?.isTile === false) {
      setDefaultOrder(structureModel);
      setFilterFromParameters(structureModel);
    }
    function setDefaultOrder(elStructModel: ElistStructureModel) {
      const fieldsWithOrder = elStructModel.elFields.filter(
        (f) =>
          f.orderByPriority !== undefined &&
          f.orderByPriority !== 0 &&
          f.orderBySort !== "N",
      );
      if (fieldsWithOrder.length > 0) {
        const orderedFieldsWithOrder = fieldsWithOrder.sort(
          (f1, f2) =>
            (f1.orderByPriority as number) - (f2.orderByPriority as number),
        );
        const elistOrders: ElistOrder[] = [];

        orderedFieldsWithOrder.forEach((f) => {
          elistOrders.push({
            id: f.fieldName as string,
            desc: f.orderBySort === "D" ? true : false,
          });
          const copySortFilterState = {
            sortBy: elistOrders.map((o) => {
              return { id: o.id, desc: o.desc };
            }),
            filters: [...sortAndFilterState.filters],
          };
          dispatch(
            setSortFilterForNode({
              nodeId: id,
              sortFilterState: copySortFilterState,
            }),
          );
        });
      }
      if (sortAndFilterState.filters.length > elistFilters.length) {
        setElistFilters(
          sortAndFilterState.filters.map((f) => {
            return { id: f.id, value: f.value };
          }),
        );
      }
    }
  }, [structureModel]);

  useEffect(() => {
    setLoading(true);
    setStructureModel(undefined);
    setViewModel(undefined);
    setFrollUrl(undefined);
    setwasEformChangedAndRefreshed(false);
    setInfoPanelHeader(undefined);
    setElistFilters([]);
    setSearchString(undefined);
    const nodeId = Number(id);
    dispatch(setActiveNodeId(nodeId));
    const jsonBody = getJsonFromParameters(urlParameters);
    const url = getUrlBasedOnEnv(
      `/api/Structure/GetNodeConfiguration/${encodeURIComponent(id ?? "")}`,
    );
    const structRunId = crypto.randomUUID();
    void fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: structRunId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: jsonBody,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, structRunId);
        } else {
          return response.json();
        }
      })
      .then(async function (dataStructure) {
        setLoading(false);
        if (!dataStructure) {
          return;
        }
        const elistStructureModel = new ElistStructureModel(dataStructure);
        setStructureModel(elistStructureModel);
        setAreaId(elistStructureModel.applAreaId);
      });
  }, [accessToken, dispatch, id, urlParameters]);

  const emptyCell = useMemo(() => {
    return <span className="placeholder-cell">--</span>;
  }, []);

  // 0/false are valid values and must not be treated as empty (only null/undefined/"" are empty).
  const hasCellValue = useCallback((value: unknown): boolean => {
    return value !== null && value !== undefined && value !== "";
  }, []);

  const getEmptyCellStyle = useCallback(
    (cellVm: CellVm): import("react").CSSProperties | undefined => {
      if (cellVm.style !== undefined) {
        return getstyleFromString(cellVm.style, false);
      } else {
        return {
          alignItems: "left",
          justifyContent: "left",
        };
      }
    },
    [],
  );

  const getCellStyle = useCallback(
    (
      cellVm: CellVm,
      center?: boolean,
    ): import("react").CSSProperties | undefined => {
      if (cellVm.style !== undefined) {
        return getstyleFromString(cellVm.style, center);
      } else {
        if (center) {
          return {
            alignItems: "center",
            justifyContent: "center",
          };
        }
      }
    },
    [],
  );

  const getItemAction = useCallback(
    (column: Column, generateShade: boolean): any => {
      return {
        Header: () => {
          return (
            <TableHeaderCellItemAction
              label={column.name}
              generateLeftShade={generateShade}
            />
          );
        },
        accessor: column.name,
        id: column.name,
        disableSortBy: true,
        disableResizing: true,
        width: column.width !== undefined ? column.width : 50,
        sticky: "right",
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell
              {...props}
              style={getCellStyle(props.cellProps.value, true)}
            >
              {props.cellProps.value.action.isVisible === true ? (
                <Action
                  action={props.cellProps.value.action}
                  key={props.cellProps.value.action.actionId}
                  actionEndedFunction={actionEnded}
                  navigateToInformationPanelFunction={
                    navigateToInfPanelFunction
                  }
                />
              ) : (
                <></>
              )}
              {generateShade && (
                <div className="becs-table-cell-shadow-left-elist" />
              )}
            </DefaultCell>
          );
        },
      };
    },
    [getCellStyle, actionEnded],
  );

  const generateColumn = useCallback(
    (column: Column, generateShade = false): any => {
      switch (column.dataType) {
        case "ItemAction":
          return getItemAction(column, generateShade);
        case "Boolean":
          return {
            Header: column.caption,
            accessor: column.name,
            sortType: customCellValueSort,
            id: column.name,
            width: column.width,
            disableToggleVisibility: column.disableToggleVisibility,
            cellRenderer: (props: CellRendererProps<any>) => {
              return (
                <DefaultCell
                  {...props}
                  style={getCheckBoxStyleFromString(props.cellProps.value)}
                >
                  {props.cellProps.value.value === true ? (
                    <IconByName name="StatusSuccessColor" />
                  ) : (
                    <IconByName name="StatusSuccessColorMuted" />
                  )}
                </DefaultCell>
              );
            },
          };

        case "Attachment":
          return {
            Header: column.caption,
            accessor: column.name,
            sortType: customCellValueSort,
            id: column.name,
            width: column.width,
            cellRenderer: (props: CellRendererProps<any>) => {
              return (
                <DefaultCell
                  {...props}
                  style={getCheckBoxStyleFromString(props.cellProps.value)}
                >
                  {props.cellProps.value.value === true ? (
                    <IconByName name="Attachment" />
                  ) : (
                    <></>
                  )}
                </DefaultCell>
              );
            },
          };

        case "DateTime":
          return {
            Header: column.caption,
            accessor: column.name,
            sortType: customCellValueSort,
            id: column.name,
            width: column.width,
            disableToggleVisibility: column.disableToggleVisibility,
            Filter: tableFilters.TextFilter(),
            cellRenderer: (props: CellRendererProps<any>) => {
              return props.cellProps.value.value ? (
                <DefaultCell
                  {...props}
                  style={getCellStyle(props.cellProps.value)}
                >
                  {FormatDate(props.cellProps.value.value)}
                </DefaultCell>
              ) : (
                <DefaultCell
                  {...props}
                  style={getEmptyCellStyle(props.cellProps.value)}
                >
                  {emptyCell}
                </DefaultCell>
              );
            },
          };

        case "Color":
          return {
            Header: column.caption,
            accessor: column.name,
            disableSortBy: true,
            id: column.name,
            width: column.width,
            disableToggleVisibility: column.disableToggleVisibility,
            cellRenderer: (props: CellRendererProps<any>) => {
              return props.cellProps.value.value ? (
                <DefaultCell
                  {...props}
                  style={getCellStyle(props.cellProps.value, true)}
                >
                  <div
                    className="circle"
                    style={{ backgroundColor: props.cellProps.value.value }}
                  />
                </DefaultCell>
              ) : (
                <DefaultCell
                  {...props}
                  style={getEmptyCellStyle(props.cellProps.value)}
                >
                  {emptyCell}
                </DefaultCell>
              );
            },
          };

        case "ImgBase64":
          return {
            Header: column.caption,
            accessor: column.name,
            disableSortBy: true,
            id: column.name,
            width: column.width,
            disableToggleVisibility: column.disableToggleVisibility,
            cellRenderer: (props: CellRendererProps<any>) => {
              return props.cellProps.value.value ? (
                <DefaultCell
                  {...props}
                  style={getCellStyle(props.cellProps.value, true)}
                >
                  <div style={{ display: "flex", height: "2rem" }}>
                    <img
                      src={`data:image/jpeg;base64,${props.cellProps.value.value}`}
                      alt={column.caption}
                    />
                  </div>
                </DefaultCell>
              ) : (
                <DefaultCell
                  {...props}
                  style={getEmptyCellStyle(props.cellProps.value)}
                >
                  {emptyCell}
                </DefaultCell>
              );
            },
          };

        default:
          return {
            Header: column.caption,
            accessor: column.name,
            sortType: customCellValueSort,
            id: column.name,
            width: column.width,
            disableToggleVisibility: column.disableToggleVisibility,
            Filter: tableFilters.TextFilter(),
            cellRenderer: (props: CellRendererProps<any>) => {
              return hasCellValue(props.cellProps.value.value) ? (
                <DefaultCell
                  {...props}
                  style={getCellStyle(props.cellProps.value)}
                >
                  {props.cellProps.value.value}
                </DefaultCell>
              ) : (
                <DefaultCell
                  {...props}
                  style={getEmptyCellStyle(props.cellProps.value)}
                >
                  {emptyCell}
                </DefaultCell>
              );
            },
          };
      }
    },
    [emptyCell, getCellStyle, getEmptyCellStyle, getItemAction, hasCellValue],
  );

  const generateColumnManagerColumn = useCallback(
    (actionColumns: Column[]): any => {
      const buttonCellTemplate = (cellProps: any) => {
        const menuItems = (close: () => void) => {
          const actionMenuItems: React.JSX.Element[] = [];
          actionColumns.forEach((ac) => {
            if (
              ac.name !== undefined &&
              cellProps.cell.row.original[ac.name].action.isVisible
            ) {
              actionMenuItems.push(
                <Action
                  action={cellProps.cell.row.original[ac.name].action}
                  key={cellProps.cell.row.original[ac.name].action.actionId}
                  actionEndedFunction={actionEnded}
                  navigateToInformationPanelFunction={
                    navigateToInfPanelFunction
                  }
                  onaActionClick={() => close()}
                />,
              );
            }
          });

          return actionMenuItems;
        };
        if (actionColumns.length > 0) {
          return (
            <>
              <DropdownMenu menuItems={menuItems}>
                <IconButton
                  styleType="borderless"
                  onClick={(e) => e.stopPropagation()}
                  label="Actions"
                >
                  <SvgMore />
                </IconButton>
              </DropdownMenu>
              <div className="becs-table-cell-shadow-left-elist" />
            </>
          );
        }
      };

      return {
        ...ActionColumn({ columnManager: true }),
        Cell: buttonCellTemplate,
        sticky: "right",
      };
    },
    [actionEnded],
  );

  const columns = useMemo(() => {
    const cols: any[] = [];
    let wasFirstSticky = false;
    const itemActionColumns: Column[] = [];
    vmColumns.forEach((column) => {
      if (column.isHidden === false) {
        if (column.dataType === "ItemAction") {
          if (column.isActionInThreeDotMenu) {
            itemActionColumns.push(column);
          } else {
            if (wasFirstSticky === false) {
              wasFirstSticky = true;
              cols.push(generateColumn(column, true));
            } else {
              cols.push(generateColumn(column));
            }
          }
        } else {
          cols.push(generateColumn(column));
        }
      }
    });
    cols.push(generateColumnManagerColumn(itemActionColumns));
    return cols;
  }, [generateColumn, generateColumnManagerColumn, vmColumns]);

  const getGlobalFilter = useCallback(
    (
      rows: Row<Record<string, unknown>>[],
      _columnIds: string[],
      filterValue: ElistFilter[],
    ): Row<Record<string, unknown>>[] => {
      if (!filterValue || filterValue.length === 0) {
        return rows;
      }

      const filteredRows = rows.filter((row: Row<Record<string, unknown>>) => {
        const rowMatches = (rowRec: Record<string, unknown>): boolean => {
          const isMatch = !filterValue.some(
            (fv) =>
              rowRec[fv.id] !== undefined &&
              (rowRec[fv.id] as CellVm).value !== undefined &&
              isValueMach((rowRec[fv.id] as CellVm).value, fv.value) === false,
          );
          return isMatch;

          function isValueMach(cellValue: string, filter: string): unknown {
            cellValue = cellValue.toUpperCase();
            filter = filter.toUpperCase();
            // ';' is  used as OR
            const indices = getIndicesOf(";", filter);

            if (indices.length > 0) {
              const filterValues = [];
              for (let i = 0; i < indices.length; i++) {
                if (i === 0) {
                  filterValues.push(filter.substring(0, indices[i]));
                } else {
                  filterValues.push(
                    filter.substring(indices[i - 1] + 1, indices[i]),
                  );
                }
                //last filter at the end
                if (i === indices.length - 1) {
                  filterValues.push(filter.substring(indices[i] + 1));
                }
              }
              let isOneFilterMatch = false;
              filterValues.forEach((fv) => {
                if (regexIsMatch(fv) === true) {
                  isOneFilterMatch = true;
                }
              });
              return isOneFilterMatch;
            } else {
              return regexIsMatch(filter);
            }

            function regexIsMatch(filterVal: string) {
              let stringForRegex = filterVal.replaceAll("%", "\\w");
              stringForRegex = stringForRegex.replaceAll("*", "\\w*");
              stringForRegex = `\\b${  stringForRegex  }\\b`;
              const regex = new RegExp(stringForRegex, "i"); // The "i" flag makes the regex case-insensitive
              const match = regex.test(cellValue);
              return match;
            }
          }
        };

        if (rowMatches(row.original)) {
          return row;
        }
        return undefined;
      });

      return filteredRows;
    },
    [],
  );

  const recordData = useMemo((): Record<string, unknown>[] => {
    const returnValues: Record<string, unknown>[] = [];

    vmValues.forEach(function (value) {
      returnValues.push(getRecord());
      function getRecord(): Record<string, unknown> {
        const returnRecord: Record<string, unknown> = {};
        if (vmColumns.length > 0) {
          for (let i = 0; i < vmColumns.length; i++) {
            returnRecord[vmColumns[i].name as string] = value[i];
          }
        }
        return returnRecord;
      }
    });

    if (searchString !== undefined && searchString !== "") {
      const firstStringColumn = vmColumns.find((c) => c.dataType === "String");
      if (firstStringColumn !== undefined) {
        const filteredData = returnValues.filter(
          (r) =>
            (r[(firstStringColumn).name as string] as CellVm).value
              .toLowerCase()
              .indexOf(searchString) > -1,
        );

        return applyFilter(filteredData);
      }
    }

    return applyFilter(returnValues);

    function applyFilter(dataToFilter: Record<string, unknown>[]) {
      // Transform returnValues into the required format for filtering
      const rows = dataToFilter.map((item) => ({ original: item })) as Row<
        Record<string, unknown>
      >[];

      // Apply the global filter to rows
      const filteredRows = getGlobalFilter(rows, [], elistFilters);

      // Transform filteredRows back to the original data format
      const filteredData = filteredRows.map((row) => row.original);

      return filteredData;
    }
  }, [searchString, vmValues, elistFilters, getGlobalFilter]);

  const onFilter = useCallback(
    async (filters: TableFilterValue<Record<string, unknown>>[]) => {
      try {
        const tf: ElistFilter[] = [];

        if (filters && filters.length > 0) {
          filters.forEach((f) => {
            tf.push(new ElistFilter(f.id, f.value));
          });
          setElistFilters(tf);
          const copySortFilterState = {
            sortBy: [...sortAndFilterState.sortBy],
            filters: tf.map((f) => {
              return { id: f.id, value: f.value };
            }),
          };
          dispatch(
            setSortFilterForNode({
              nodeId: id,
              sortFilterState: copySortFilterState,
            }),
          );
        } else {
          setElistFilters(tf);
          const copySortClearFilterState = {
            sortBy: [...sortAndFilterState.sortBy],
            filters: [],
          };
          dispatch(
            setSortFilterForNode({
              nodeId: id,
              sortFilterState: copySortClearFilterState,
            }),
          );
        }
      } catch {
        // Error is non-actionable; sort/filter state dispatch already attempted
      }
    },
    [dispatch, id, sortAndFilterState.sortBy],
  );

  function headerBackAction(): React.JSX.Element | undefined {
    if (viewModel?.headerBackAction !== undefined) {
      return (
        <Action
          action={viewModel.headerBackAction}
          key={viewModel.headerBackAction.actionId}
          navigateToInformationPanelFunction={navigateToInfPanelFunction}
        />
      );
    }
  }

  const headerButtons = viewModel?.headerActions.map((action) => (
    <Action
      action={action}
      key={action.actionId}
      navigateToInformationPanelFunction={navigateToInfPanelFunction}
    />
  ));

  const getIcon = useCallback(
    (base64String: string, caption: any): import("react").ReactNode => {
      return (
        <div style={{ width: "9.5rem", height: "9.5rem" }}>
          <img src={`data:image/jpeg;base64,${base64String}`} alt={caption} />
        </div>
      );
    },
    [],
  );

  const tiles = useMemo((): React.JSX.Element[] | undefined => {
    if (viewModel !== undefined && viewModel.isTile === true) {
      const tileNameIndex = viewModel.columns.findIndex(
        (c) => c.isTileName === true,
      );
      const tileImgIndex = viewModel.columns.findIndex(
        (c) => c.dataType === "ImgBase64",
      );
      if (tileNameIndex === -1 || tileImgIndex === -1) {
        setErrorState(500, dispatch, undefined, true);
      } else {
        const actionIndex = viewModel.columns.findIndex(
          (c) => c.dataType === "ItemAction",
        );
        return viewModel?.values.map((valueVm) => (
          <Tile
            key={valueVm[tileNameIndex].value}
            isActionable
            name={valueVm[tileNameIndex].value}
            onClick={() => {
              if (actionIndex !== -1) {
                if (
                  valueVm[actionIndex].action != null &&
                  valueVm[actionIndex].action !== undefined
                ) {
                  const actionVm = valueVm[actionIndex].action;
                  const actionProps = {
                    action: actionVm,
                    key: actionVm.actionId,
                    actionEndedFunction: actionEnded,
                  };
                  void actionVm.executeAction(
                    () => setLoading(true),
                    () => setLoading(false),
                    actionProps,
                    (navStr: string) => {
                      navStr = navStr + window.location.search;
                      void navigate(navStr);
                    },
                    accessToken,
                    dispatch,
                  );
                }
              }
            }}
            thumbnail={getIcon(
              valueVm[tileImgIndex].value,
              valueVm[tileNameIndex].value,
            )}
            variant="default"
          />
        ));
      }
    }
  }, [accessToken, actionEnded, dispatch, getIcon, navigate, viewModel]);

  const variantButtons = useMemo((): React.JSX.Element[] | undefined => {
    //if only one viariant, do not show button
    if (viewModel !== undefined && viewModel.variants.length > 1) {
      return viewModel?.variants.map((variant) => (
        <Variant
          key={variant.id}
          variant={variant}
          clickFunction={async () => {
            setActiveVariantId(variant.id);
          }}
        />
      ));
    }
  }, [viewModel]);

  const pageSizeList = useMemo(() => [10, 25, 50, 100], []);
  const paginator = useCallback(
    (props: TablePaginatorRendererProps) => (
      <TablePaginator
        {...props}
        pageSizeList={pageSizeList}
        isLoading={false}
      />
    ),
    [pageSizeList],
  );

  /**translate parameters defined as [[parameterName]] to theirs values */
  function getHeader(): string | undefined {
    if (viewModel !== undefined && viewModel.header !== undefined) {
      return getTranslatedStringFromParameters(viewModel.header, urlParameters);
    } else {
      return viewModel?.header;
    }
  }

  const getFormIdFromFrollNavUrl = useMemo((): number | string | undefined => {
    if (!fRollUrl) {
      return undefined;
    }

    try {
      // Remove leading slash and split into segments
      const segments = fRollUrl.substring(1).split("/");

      // Handle /eform/329/params format
      if (segments[0] === "eform") {
        const formId = parseInt(segments[1], 10);
        if (isNaN(formId)) {
          return undefined;
        }
        return formId;
      }

      // Handle /componentIdentifier/params format
      // Return just the component identifier (first segment)
      return segments[0];
    } catch {
      return undefined;
    }
  }, [fRollUrl]);

  const getParamsFromFrollNavUrl = useMemo(() => {
    if (fRollUrl === undefined) {
      return undefined;
    } else {
      //e.g. /eform/329/MICSPID=26&PROJECT_NAME=IL_FROM_ALIM_TO_SHAREPOINT
      const hasParams = fRollUrl.indexOf("=") !== -1;
      const paramSubstring = hasParams
        ? fRollUrl.substring(fRollUrl.lastIndexOf("/") + 1)
        : undefined;
      return paramSubstring;
    }
  }, [fRollUrl]);

  const renderEform = useCallback(
    (formId: number) => {
      return (
        <Eform
          isFromUrl={false}
          id={formId.toString()}
          params={getParamsFromFrollNavUrl}
          closeFrollFunction={onFrollClose}
          navigateToInformationPanelFunction={navigateToInfPanelFunction}
          setInforPanelHeaderFunction={onSetInfoPanelHeader}
          refreshInRollOutWindowProperty={propertyForFrollRefresh}
          wasEformChangedAndRefreshed={wasEformChangedAndRefreshed}
        />
      );
    },
    [
      getParamsFromFrollNavUrl,
      onFrollClose,
      navigateToInfPanelFunction,
      onSetInfoPanelHeader,
      propertyForFrollRefresh,
      wasEformChangedAndRefreshed,
    ],
  );

  const renderCustomComponent = useCallback(
    (identifier: string) => {
      const Component = getComponentByIdentifier(identifier);
      if (!Component) {
        return <div>Component not found: {identifier}</div>;
      }
      return (
        <Component
          closeFrollFunction={onFrollClose}
          navigateToInformationPanelFunction={navigateToInfPanelFunction}
          parameters={getParamsFromFrollNavUrl}
          setInforPanelHeaderFunction={onSetInfoPanelHeader}
        />
      );
    },
    [
      onFrollClose,
      navigateToInfPanelFunction,
      getParamsFromFrollNavUrl,
      onSetInfoPanelHeader,
    ],
  );

  const renderDynamicComponent = useMemo(() => {
    if (typeof getFormIdFromFrollNavUrl === "number") {
      return renderEform(getFormIdFromFrollNavUrl);
    } else if (typeof getFormIdFromFrollNavUrl === "string") {
      return renderCustomComponent(getFormIdFromFrollNavUrl);
    }
    return null;
  }, [getFormIdFromFrollNavUrl, renderEform, renderCustomComponent]);

  function getZindex(): number {
    return confirmationState.isConfirmationOpen ? 0 : 2;
  }

  return (
    <div style={{ height: "100%" }}>
      <InformationPanelWrapper
        style={{
          height: "100%",
          maxWidth: "100%",
          borderRadius: 0,
        }}
      >
        <div>
          {fRollUrl !== undefined && <div className="overlay" />}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={
                fRollUrl !== undefined
                  ? { pointerEvents: "none", opacity: "0.8" }
                  : {}
              }
            >
              {!isLoading && (
                <div className="becs-infopanelbody-footer-line">
                  <div className="becs-all-margin" ref={headerDivRef}>
                    <div className="rowWrap">
                      {headerBackAction()}
                      {viewModel?.header !== undefined &&
                        viewModel?.header !== "" && (
                          <Text variant="title">{getHeader()}</Text>
                        )}
                    </div>
                  </div>
                </div>
              )}

              <div className="becs-ltr-margin">
                {!isLoading && (
                  <div>
                    <div
                      className="horizontal becs-tb-margin"
                      ref={headerButtonsDivRef}
                    >
                      <div className="leftAlign">
                        {viewModel?.headerActions !== undefined &&
                          viewModel?.headerActions.length > 0 && (
                            <div className="rowWrap">{headerButtons}</div>
                          )}
                      </div>

                      <div className="rightAlign">
                        <div className="rowWrap">
                          <SearchBox
                            style={{ width: "15em" }}
                            inputProps={{
                              placeholder: "Search...",
                              onChange: searchBoxOnChange,
                              value: searchString,
                            }}
                          />
                          {viewModel?.isLegendVisible && (
                            <div>
                              <Button onClick={() => setIsLegendOpen(true)}>
                                <div className="rowWrap">
                                  <SvgLabel height="25" />
                                  <Text variant="body">Legend</Text>
                                </div>
                              </Button>
                              {isLegendOpen && (
                                <LegendModal
                                  isOpen={isLegendOpen}
                                  onCancel={() => setIsLegendOpen(false)}
                                  onDelete={() => {
                                    setIsLegendOpen(false);
                                  }}
                                  highlights={viewModel?.legendHighlights}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rowWrapNoGap" ref={variantButtonsDivRef}>
                      {variantButtons}
                    </div>
                  </div>
                )}
                {isLoading ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <ProgressRadial indeterminate />
                  </div>
                ) : (
                  viewModel !== undefined && (
                    <div
                      style={{
                        maxWidth: `calc(100vw - ${sideMenuWidth}px)`,
                      }}
                    >
                      {viewModel.isTile === false ? (
                        <Table
                          columns={columns}
                          emptyTableContent="No data."
                          data={recordData}
                          isLoading={isLoading}
                          style={{
                            height: `calc(100vh - ${mainMenuHeight + headerDivHeight + headerButtonsDivHeight + variantButtonsDivHeight}px)`,
                            maxWidth: `calc(100vw - ${sideMenuWidth}px)`,
                          }}
                          isSortable={true}
                          isResizable={false}
                          density="extra-condensed"
                          styleType="zebra-rows"
                          paginatorRenderer={paginator}
                          pageSize={viewModel?.pageSize}
                          initialState={sortAndFilterState}
                          onFilter={onFilter}
                          globalFilter={getGlobalFilter}
                          globalFilterValue={elistFilters}
                          manualFilters={true}
                        />
                      ) : (
                        <div className="rowWrap">{tiles}</div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
        {fRollUrl !== undefined && (
          <InformationPanel
            resizable
            isOpen={fRollUrl !== undefined}
            style={{
              width: "40%",
              maxWidth: "70%",
              minWidth: "10%",
              zIndex: getZindex(),
            }}
            className="iui-infoPanel"
          >
            <InformationPanelHeader
              style={{
                padding: 0,
              }}
            >
              <div className="becs-all-margin">
                <Text variant="title">{infoPanelHeader}</Text>
              </div>
            </InformationPanelHeader>
            <InformationPanelBody
              style={{
                padding: 0,
              }}
            >
              <InformationPanelContent
                displayStyle="default"
                style={{
                  padding: 0,
                  height: "100%",
                }}
              >
                {renderDynamicComponent}
              </InformationPanelContent>
            </InformationPanelBody>
          </InformationPanel>
        )}
      </InformationPanelWrapper>
    </div>
  );
};

export default Elist;
